import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, adminAuth } from "@/lib/api";
import { formatApiError } from "@/lib/formatError";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const nextRaw = params.get("next") || "";
  const nextSafe = nextRaw && nextRaw.startsWith("/") && !nextRaw.startsWith("//") ? nextRaw : "";
  const expired = params.get("expired") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/admin/login", { email, password });
      adminAuth.setSession({
        token: data.token,
        email: data.email,
        role: data.admin_role,
        practitioner_id: data.practitioner_id,
        name: data.name,
        subscription_tier: data.subscription_tier,
      });
      toast.success("Welcome");
      // Honor ?next= redirect if it points to an admin route the user is allowed to see
      if (nextSafe) {
        navigate(nextSafe);
      } else if (data.admin_role === "super_admin") {
        navigate("/admin");
      } else {
        navigate("/practitioner");
      }
    } catch (err) {
      toast.error(formatApiError(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <form onSubmit={submit} className="w-full max-w-md kcis-card p-8 sm:p-10 kcis-fade-up">
          <div className="h-12 w-12 rounded-2xl bg-purple-50 text-purple-900 flex items-center justify-center mb-5">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div className="kcis-overline text-purple-900">Admin access</div>
          <h1 className="font-display text-3xl tracking-tight text-slate-900 mt-2">Admin sign-in</h1>

          {expired && (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900" data-testid="admin-login-expired">
              Your previous admin session expired. Please sign in again to continue.
            </div>
          )}

          <div className="mt-7 space-y-4">
            <div>
              <Label className="text-sm text-slate-700">Email</Label>
              <Input data-testid="admin-login-email" type="email" autoComplete="email" className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div>
              <Label className="text-sm text-slate-700">Password</Label>
              <Input data-testid="admin-login-password" type="password" autoComplete="current-password" className="mt-2 rounded-xl border-slate-200 bg-slate-50/60 h-12" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-purple-900 hover:bg-purple-800 text-white h-12" data-testid="admin-login-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
            <div className="text-center">
              <Link to="/admin/forgot-password" className="text-xs text-purple-900 hover:underline" data-testid="admin-forgot-password-link">
                Forgot password?
              </Link>
            </div>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}
